import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useAuth} from '@/contexts/AuthContext';
import {ReitService as reitService} from '@/services/investments';
import {RecordREITDividendDto} from "@/@types/reit.dto";

/**
 * Custom hook to execute a mutation for recording REIT dividend payments.
 * It interacts with the REIT service to record dividends for the currently authenticated user
 * and invalidates relevant queries to ensure cached data is updated.
 *
 * @return {UseMutationResult} A mutation result object that includes methods and state for tracking the mutation,
 * such as `mutate`, `mutateAsync`, and mutation status.
 */
export default function useRecordReitDividend() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: RecordREITDividendDto) => {
      return await reitService.recordDividend(user.id, input);
    },
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['reit_dividend_payments'] });
      qc.invalidateQueries({ queryKey: ['reit_holdings'] });
      if (input.dividend_action === 'deposit_to_account') {
        qc.invalidateQueries({ queryKey: ['accounts'] });
        qc.invalidateQueries({ queryKey: ['transactions'] });
      }
    },
  });
}
